// A visitor already signed in to noBGP — on the app, or on any protected
// `*.nobgp.link` service — must reach the app without a click (Benoit,
// 2026-09-21). Since router#339 a service login no longer sets the `.nobgp.com`
// cookies the app reads, so the app does what a protected service does: it
// sends the browser to AuthKit once by itself, and AuthKit, which holds the
// sign-in, sends it straight back. A visitor who is not signed in lands on
// AuthKit's own sign-in page, which also offers sign-up.
//
// ⚠ Three cases must NOT bounce, or the page traps its visitor:
//  - an error on the page: AuthKit just refused, and bouncing hides why;
//  - a sign-out in this tab: the visitor asked to leave, and the app's own
//    sign-in page is where they expect to land;
//  - an attempt in the last RETRY_AFTER_MS: pressing Back on AuthKit returns
//    here, and bouncing again would make Back useless.

export const RETRY_AFTER_MS = 60_000

export type SilentSignInInputs = {
  initialized: boolean
  hasSession: boolean
  hasError: boolean
  signedOutInThisTab: boolean
  lastAttemptAt: number | null
  now: number
}

export function shouldTrySilentSignIn(i: SilentSignInInputs): boolean {
  if (!i.initialized || i.hasSession || i.hasError || i.signedOutInThisTab) return false
  return i.lastAttemptAt === null || i.now - i.lastAttemptAt >= RETRY_AFTER_MS
}

// Per-tab memory. A browser that refuses storage reads as "never attempted, not
// signed out"; the RETRY guard is then lost, which costs a Back-button bounce and
// nothing worse.
const ATTEMPT_KEY = 'nobgp-silent-signin-at'
const SIGNED_OUT_KEY = 'nobgp-signed-out'

const read = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}
const write = (key: string, value: string | null) => {
  try {
    if (value === null) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, value)
  } catch {
    // the guard is a convenience
  }
}

export const lastSilentAttempt = (): number | null => {
  const v = Number(read(ATTEMPT_KEY))
  return Number.isFinite(v) && v > 0 ? v : null
}
export const markSilentAttempt = (now: number) => write(ATTEMPT_KEY, String(now))
export const signedOutInThisTab = (): boolean => read(SIGNED_OUT_KEY) !== null
export const markSignedOut = () => write(SIGNED_OUT_KEY, '1')
// A click on Sign in is the visitor coming back: silent attempts resume.
export const clearSignedOut = () => write(SIGNED_OUT_KEY, null)
